import React, { useEffect } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { allBlogPosts } from "../data/blog";
import { useSEO, generateBlogPostSchema, addStructuredData, removeStructuredData } from "../utils/seo";
import Navigation from "../components/Navigation";
import Footer from "../components/Footer";

const BlogDetail: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const post = allBlogPosts.find(p => p.slug === slug);

  // SEO for blog post - must be called unconditionally
  useSEO({
    title: post ? `${post.title} | Alertaio Blog` : 'Blog Post | Alertaio',
    description: post ? post.metaDescription : 'AI API cost monitoring insights and best practices',
    keywords: post ? post.tags.join(', ') : 'AI API costs, OpenAI, monitoring',
    canonicalUrl: post ? `https://alertaio.com/blog/${post.slug}` : 'https://alertaio.com/blog',
    type: 'article',
    publishedTime: post?.publishedDate,
    author: post?.author
  });

  useEffect(() => {
    if (post) {
      const schema = generateBlogPostSchema({
        title: post.title,
        description: post.metaDescription,
        author: post.author,
        publishedDate: post.publishedDate,
        url: `https://alertaio.com/blog/${post.slug}`
      });
      addStructuredData(schema);
    }

    return () => {
      removeStructuredData();
    };
  }, [post]);

  if (!post) {
    return <Navigate to="/blog" replace />;
  }

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  };

  const relatedPosts = post.relatedPosts
    .map(slug => allBlogPosts.find(p => p.slug === slug))
    .filter(Boolean)
    .slice(0, 3);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-indigo-50">
      <Navigation />

      {/* Breadcrumb */}
      <nav className="bg-white border-b border-slate-200 py-4">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <ol className="flex items-center gap-2 text-sm">
              <li>
                <Link to="/" className="text-indigo-600 hover:text-indigo-700">Home</Link>
              </li>
              <li className="text-slate-400">/</li>
              <li>
                <Link to="/blog" className="text-indigo-600 hover:text-indigo-700">Blog</Link>
              </li>
              <li className="text-slate-400">/</li>
              <li className="text-slate-600 truncate">{post.category}</li>
            </ol>
          </div>
        </div>
      </nav>

      {/* Article Header */}
      <header className="bg-gradient-to-br from-indigo-600 via-blue-600 to-indigo-700 text-white py-16 md:py-20">
        <div className="container mx-auto px-4">
          <article className="max-w-4xl mx-auto">
            {/* Category Badge */}
            <div className="mb-6">
              <span className="inline-block bg-white/20 backdrop-blur-sm text-white text-sm font-semibold px-4 py-1.5 rounded-full">
                {post.category}
              </span>
            </div>

            {/* Title - Only H1 on page */}
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold mb-6 leading-tight">
              {post.title}
            </h1>

            {/* Meta Info */}
            <div className="flex flex-wrap items-center gap-6 text-blue-100">
              <div className="flex items-center gap-2">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                </svg>
                <span>By {post.author}</span>
              </div>
              <div className="flex items-center gap-2">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                <time dateTime={post.publishedDate}>{formatDate(post.publishedDate)}</time>
              </div>
              <div className="flex items-center gap-2">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span>{post.readTime} min read</span>
              </div>
            </div>

            {/* Tags */}
            <div className="mt-6 flex flex-wrap gap-2">
              {post.tags.map(tag => (
                <span key={tag} className="bg-white/10 backdrop-blur-sm text-white text-xs font-medium px-3 py-1 rounded-full">
                  #{tag}
                </span>
              ))}
            </div>
          </article>
        </div>
      </header>

      {/* Article Content */}
      <main className="py-16">
        <div className="container mx-auto px-4">
          <article className="max-w-4xl mx-auto">
            {/* Introduction */}
            <div className="bg-gradient-to-br from-indigo-50 to-blue-50 border-l-4 border-indigo-600 rounded-r-xl p-6 md:p-8 mb-12">
              <div className="text-lg md:text-xl text-slate-700 leading-relaxed space-y-4">
                {post.content.introduction.split('\n\n').map((paragraph, pIdx) => (
                  <p key={pIdx}>{paragraph}</p>
                ))}
              </div>
            </div>

            {/* Main Content */}
            <div className="prose prose-lg max-w-none">
              {post.content.sections.map((section, index) => (
                <section key={index} className="mb-12">
                  {/* H2 Section Heading */}
                  <h2 className="text-3xl font-bold text-slate-900 mb-4 pb-2 border-b-2 border-indigo-200">
                    {section.heading}
                  </h2>

                  {/* H3 Subheading (if exists) */}
                  {section.subheading && (
                    <h3 className="text-2xl font-semibold text-slate-800 mb-3 mt-6">
                      {section.subheading}
                    </h3>
                  )}

                  {/* Section Content */}
                  <div className="text-slate-700 leading-relaxed mb-6 space-y-4">
                    {section.content.split('\n\n').map((paragraph, pIdx) => (
                      <p key={pIdx}>{paragraph}</p>
                    ))}
                  </div>

                  {/* Bullet Points (if exists) */}
                  {section.bulletPoints && section.bulletPoints.length > 0 && (
                    <ul className="space-y-3 mb-6">
                      {section.bulletPoints.map((point, idx) => (
                        <li key={idx} className="flex items-start gap-3">
                          <svg className="w-6 h-6 text-indigo-600 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                          </svg>
                          <span className="text-slate-700">{point}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </section>
              ))}

              {/* Conclusion */}
              <section className="mt-12 bg-slate-50 rounded-2xl p-6 md:p-8 border border-slate-200">
                <h2 className="text-2xl font-bold text-slate-900 mb-4 flex items-center gap-2">
                  <svg className="w-6 h-6 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  Conclusion
                </h2>
                <div className="text-slate-700 leading-relaxed space-y-4">
                  {post.content.conclusion.split('\n\n').map((paragraph, pIdx) => (
                    <p key={pIdx}>{paragraph}</p>
                  ))}
                </div>
              </section>
            </div>

            {/* CTA Section */}
            <div className="mt-16 bg-gradient-to-br from-indigo-600 via-blue-600 to-indigo-700 rounded-3xl p-8 md:p-12 text-center text-white relative overflow-hidden">
              <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full blur-3xl"></div>
              <div className="absolute bottom-0 left-0 w-48 h-48 bg-blue-400/20 rounded-full blur-2xl"></div>
              
              <div className="relative z-10">
                <h2 className="text-3xl md:text-4xl font-bold mb-4">
                  Ready to Monitor Your AI Costs?
                </h2>
                <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
                  Join the waitlist to monitor your AI API costs with Alertaio. Get real-time alerts and prevent overspending.
                </p>
                <Link
                  to="/#bottom-cta"
                  className="inline-block bg-white text-indigo-600 hover:bg-gray-50 font-bold px-10 py-4 rounded-full text-lg transition-all transform hover:scale-105 shadow-2xl"
                >
                  Get Started Free
                </Link>
              </div>
            </div>

            {/* Related Posts */}
            {relatedPosts.length > 0 && (
              <section className="mt-20">
                <h2 className="text-3xl font-bold text-slate-900 mb-8 flex items-center gap-3">
                  <svg className="w-8 h-8 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                  Related Articles
                </h2>
                <div className="grid md:grid-cols-3 gap-6">
                  {relatedPosts.map((relatedPost) => relatedPost && (
                    <Link
                      key={relatedPost.slug}
                      to={`/blog/${relatedPost.slug}`}
                      className="bg-white rounded-xl border border-slate-200 p-6 hover:shadow-lg transition-all group"
                    >
                      <span className="inline-block bg-indigo-100 text-indigo-700 text-xs font-semibold px-3 py-1 rounded-full mb-3">
                        {relatedPost.category}
                      </span>
                      <h3 className="text-lg font-bold text-slate-900 mb-2 group-hover:text-indigo-600 transition-colors line-clamp-2">
                        {relatedPost.title}
                      </h3>
                      <p className="text-sm text-slate-600 line-clamp-2">
                        {relatedPost.excerpt}
                      </p>
                      <div className="mt-4 flex items-center gap-1 text-indigo-600 text-sm font-semibold">
                        Read More
                        <svg className="w-4 h-4 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </div>
                    </Link>
                  ))}
                </div>
              </section>
            )}
          </article>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default BlogDetail;
